import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import StarIcon from '@mui/icons-material/Star';

import React from 'react';

import { RoleEnum, UserListDTO } from '_lib/api/apiTypes.shim';
import { Avatar } from '_lib/components/Avatar/Avatar';

import { IconButton } from '_lib/components/IconButton/IconButton';
import { LinkListItemButton } from '_lib/components/LinkListItemButton/LinkListItemButton';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemAvatar } from '_lib/components/ListItemAvatar/ListItemAvatar';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';

import { Typography } from '_lib/components/Typography/Typography';

import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { logDebug } from '_lib/utils/logging';

import { useOurSnackbar } from '_lib/utils/snackUtils';

type UserListItemProps = {
  user: UserListDTO;
  href: string;
  onClick: (user: UserListDTO) => Promise<void>;
  onDelete: (user: UserListDTO) => Promise<void>;
  onReactivate: (user: UserListDTO) => Promise<void>;
  disabled?: boolean;
};

/** Note that this component is used for both AccountUser and User */
export const UserListItem = (props: UserListItemProps) => {
  const { user, href, onClick, onDelete, onReactivate, disabled } = props;

  const confirmationDialog = useConfirmationDialog();
  const { enqueueSnackbar } = useOurSnackbar();

  return (
    <>
      <ListItem
        key={user.id}
        divider
        disablePadding
        secondaryAction={
          <Tooltip title={`${user.isActive ? 'Deactivate' : 'Reactivate'} User`} placement='left'>
            <IconButton
              disabled={disabled}
              onClick={async () => {
                const result = await confirmationDialog.show({
                  titleText: `${user.isActive ? 'Deactivate' : 'Reactivate'} User?`,
                  contentText: ''
                });
                if (result?.action === 'CANCEL') return;

                try {
                  if (user.isActive) {
                    await onDelete(user);
                  } else {
                    await onReactivate(user);
                  }
                  enqueueSnackbar(`user ${user.isActive ? 'deactivated' : 'reactivated'}`);
                } catch (err) {
                  enqueueSnackbar(`user could not be ${user.isActive ? 'deactivated' : 'reactivated'}`);
                  logDebug(err);
                }
              }}>
              {user.isActive ? <DeleteIcon /> : <SettingsBackupRestoreIcon />}
            </IconButton>
          </Tooltip>
        }>
        <LinkListItemButton
          href={href}
          onClick={() => {
            onClick(user);
          }}>
          <ListItemAvatar>
            <Avatar>{user.role === RoleEnum.USER ? <PersonIcon /> : <StarIcon />}</Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <>
                <Typography component='span' text={`${user.firstName} ${user.lastName}`} />
                {!user.isActive && (
                  <Typography component='span' text='INACTIVE' color='error' sx={{ paddingLeft: 1 }} />
                )}
              </>
            }
            secondary={<Typography component='span' text={`${user.role} - ${user.email}`} />}
          />
        </LinkListItemButton>
      </ListItem>
      {confirmationDialog.element}
    </>
  );
};
